import React, { useEffect, useState } from 'react';
import { post } from 'axios';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { renderToast } from 'components/Informations/Toast/utils';
import { contentSvgs } from 'assets/icons';

import ActionsBox from 'components/ActionsBox';
import SelectField from 'components/FormGroup/SelectField';

import ContentLoader from 'components/Informations/Loaders/ContentLoader';
import Tooltip from 'components/Informations/Tooltip';

import endpoints from 'services/endpoints';
import useFetch from 'hooks/useFetch';

import { renderOverlay, getStatusCode, getActionCode } from './utils';
import * as S from './styles';

const {
  getEmployeesToSelect,
  releaseEvaluation,
  requestMemberSolicitation,
  getJustifications,
} = endpoints;

const PersonalDevelopmentTable = ({ data, setObserver, userId }) => {
  const [overlayType, setOverlayType] = useState('');
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayData, setOverlayData] = useState([]);

  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    autoClose: 6000,
  });
  const [toastVisibility, setToastVisibility] = useState(false);

  const [loading, setLoading] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [selectedMediators, setSelectedMediators] = useState([]);
  const [getEmployees] = useFetch(getEmployeesToSelect);

  const [
    releaseEvaluationJustifications,
    setReleaseEvaluationJustifications,
  ] = useState([]);
  const [
    requestEvaluationJustifications,
    setRequestEvaluationJustifications,
  ] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [currentTableFilter, setCurrentTableFilter] = useState('employee');

  useEffect(() => {
    const orderedData = _.orderBy(data, 'nome_avaliado');
    setTableData(orderedData || []);
  }, [data]);

  useEffect(() => {
    (async () => {
      const allEmployees = await getEmployees;
      const options =
        allEmployees &&
        allEmployees
          .filter(
            ({ id_funcionario: idFuncionario }) => idFuncionario !== userId,
          )
          .map(employee => ({
            value: employee.id_funcionario,
            label: employee.nome,
          }));

      const allJustifications = await fetch(getJustifications)
        .then(response => response.json())
        .then(responseJson => responseJson);

      const releaseJustifications = allJustifications
        .filter(({ tipo_justificativa: tipo }) => tipo === 'Liberação')
        .map(release => ({
          label: release.descricao,
          name: release.id_justificativa,
          value: release.id_justificativa,
          ...release,
        }));

      const requestJustifications = allJustifications
        .filter(
          ({ tipo_justificativa: tipo, escopo }) =>
            tipo === 'Solicitação' && escopo === 'Avaliação',
        )
        .map(mediation => ({
          label: mediation.descricao,
          name: mediation.id_justificativa,
          value: mediation.id_justificativa,
          ...mediation,
        }));

      setReleaseEvaluationJustifications(releaseJustifications);
      setRequestEvaluationJustifications(requestJustifications);

      setEmployees(options);

      return options.length ? setLoading(false) : setLoading(true);
    })();
  }, [getEmployees, userId]);

  const changeTableOrder = orderBy => {
    setSelectedMediators([]);

    switch (orderBy) {
      case 'employee':
        setCurrentTableFilter('employee');
        return setTableData(_.orderBy(tableData, 'nome_avaliado'));
      case 'status':
        setCurrentTableFilter('status');
        return setTableData(_.orderBy(tableData, 'descricao'));
      default:
        return null;
    }
  };

  const handleAssignMediator = async (id, config) => {
    setLoading(true);

    const itemToAssign = selectedMediators.find(
      ({ idIndex }) => idIndex === id,
    );
    const { idMediator } = itemToAssign;

    const objectToSend = {
      id_configuracao_avaliacao: config,
      id_usr_in: userId,
      id_funcionario_indicado: idMediator,
      tipo_solicitacao: 'Mediador',
    };

    try {
      await post(requestMemberSolicitation, objectToSend)
        .then(() => setLoading(false))
        .then(() => setToastVisibility(true))
        .then(() =>
          setToastInfos({
            type: 'success',
            message: 'Mediador atribuído com sucesso',
            autoClose: 4000,
          }),
        );
    } catch {
      setLoading(false);
      setToastVisibility(true);
      setToastInfos({
        type: 'error',
        message: 'Erro ao atribuir mediador. Tente novamente',
        autoClose: 6000,
      });
    }
  };

  const handleReleaseEvaluation = async content => {
    const { id_funcionario_avaliado: idFuncionario, selectedOption } = content;

    await post(releaseEvaluation, {
      id_funcionario_avaliado: idFuncionario,
      id_responsavel: userId,
      id_justificativa: selectedOption,
    });
  };

  const handleRequestEvaluation = async content => {
    const {
      id_configuracao_avaliacao: idConfig,
      selectedEmployee,
      selectedOption,
    } = content;

    const objectToSend = {
      id_configuracao_avaliacao: idConfig,
      id_usr_in: userId,
      id_funcionario_indicado: selectedEmployee,
      id_justificativa_gestor: selectedOption,
      tipo_solicitacao: 'Avaliador',
    };

    await post(requestMemberSolicitation, objectToSend);
  };

  return (
    <>
      {overlayOpen &&
        renderOverlay(overlayData, overlayType, overlayOpen, setOverlayOpen)}

      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.autoClose,
          setToastVisibility,
        )}

      <S.Table>
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col"> </th>
                <th scope="col" onClick={() => changeTableOrder('employee')}>
                  Funcionário
                  {currentTableFilter === 'employee' &&
                    contentSvgs('tableArrow')}
                </th>
                <th scope="col">Mediador</th>
                <th scope="col" onClick={() => changeTableOrder('status')}>
                  Status
                  {currentTableFilter === 'status' && contentSvgs('tableArrow')}
                </th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((teamMember, index) => {
                const {
                  descricao: status,
                  acoes: acao,
                  id_funcionario_avaliado: idFuncionario,
                  justificativa_funcionario: justificativa,
                  id_funcionario_mediador: idMediador,
                  id_configuracao_avaliacao: idConfig,
                  nome_avaliado: nomeFuncionario,
                } = teamMember;

                const statusCode = getStatusCode(status);
                const actionCode = getActionCode(acao);

                const isButtonDisabled =
                  statusCode === 0 ||
                  statusCode === 4 ||
                  (statusCode === 1 && actionCode !== 2) ||
                  (statusCode === 2 && actionCode === 3);

                const isSelectDisabled = !(
                  statusCode === 5 ||
                  (statusCode === 1 && actionCode === 2)
                );

                const currentMediator = selectedIndex => {
                  if (!selectedMediators.length) return '';

                  const requiredMediator = selectedMediators.filter(
                    ({ idIndex }) => idIndex === selectedIndex,
                  );

                  if (requiredMediator && requiredMediator.length > 0) {
                    const { idMediator } = requiredMediator[0];
                    return idMediator;
                  }

                  return '';
                };

                const redirections = [
                  {
                    onClick: () => {
                      setOverlayType('assignEvaluator');
                      setOverlayOpen(true);
                      setOverlayData({
                        employeeName: nomeFuncionario,
                        requestAction: handleRequestEvaluation,
                        justifications: requestEvaluationJustifications,
                        additionalContent: {
                          id_configuracao_avaliacao: idConfig,
                        },
                        setObserver,
                      });
                    },
                    label: 'Atribuir avaliador',
                    disabled:
                      isButtonDisabled || statusCode === 3 || statusCode === 5,
                  },
                  {
                    onClick: () => {
                      setOverlayType('releaseEvaluation');
                      setOverlayOpen(true);
                      setOverlayData({
                        employeeName: nomeFuncionario,
                        requestAction: handleReleaseEvaluation,
                        justifications: releaseEvaluationJustifications,
                        additionalContent: {
                          id_funcionario_avaliado: idFuncionario,
                        },
                        setObserver,
                      });
                    },
                    label: 'Liberar avaliação',
                    disabled: !(statusCode === 0 || statusCode === 3),
                  },
                ];

                return (
                  <tr key={idConfig}>
                    <td>
                      {contentSvgs('moreInfos')}
                      <ActionsBox
                        hasLinks={false}
                        headText="Ações"
                        redirections={redirections}
                      />
                    </td>
                    <td>{nomeFuncionario}</td>
                    <td>
                      <SelectField
                        name={`mediator-${index}`}
                        placeholderText="Insira o nome do mediador"
                        hasLabel={false}
                        labelText=""
                        options={employees}
                        value={employees.find(({ value }) =>
                          idMediador
                            ? value === currentMediator(index) ||
                              value === idMediador
                            : '',
                        )}
                        handleChange={({ value }) => {
                          setSelectedMediators(currentMediators => [
                            ...currentMediators,
                            {
                              idIndex: index,
                              idMediator: value,
                            },
                          ]);

                          return setSelectedMediators(currentMediators => {
                            const removeDuplicates = _.uniqBy(
                              [...currentMediators].reverse(),
                              'idIndex',
                            );
                            return _.orderBy(removeDuplicates, ['idIndex']);
                          });
                        }}
                        isDisabled={isSelectDisabled}
                      />
                    </td>
                    <td className={`status-${statusCode}`}>
                      {status}
                      {justificativa && (
                        <Tooltip headText="Motivo da recusa">
                          <p>{justificativa}</p>
                        </Tooltip>
                      )}
                    </td>
                    <td className={isButtonDisabled ? 'not-allowed' : ''}>
                      {statusCode === 3 ||
                      (statusCode === 2 && actionCode === 4) ? (
                        <S.LinkAction
                          to={{
                            pathname:
                              statusCode === 3
                                ? '/auth/pdi/results'
                                : '/auth/pdi/tutorial',
                            state:
                              statusCode !== 3
                                ? {
                                    context: 'team-evaluation',
                                    data: { idConfig, idFuncionario },
                                  }
                                : '',
                          }}
                        >
                          {acao}
                        </S.LinkAction>
                      ) : (
                        <S.ButtonAction
                          onClick={() => handleAssignMediator(index, idConfig)}
                          disabled={isButtonDisabled || !currentMediator(index)}
                        >
                          {!currentMediator(index) && !isButtonDisabled
                            ? ''
                            : acao}
                        </S.ButtonAction>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </S.Table>

      {loading && <ContentLoader isFull />}
    </>
  );
};

export default PersonalDevelopmentTable;

PersonalDevelopmentTable.defaultProps = {
  data: [],
  history: {},
};

PersonalDevelopmentTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ),
  setObserver: PropTypes.bool.isRequired,
  history: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ),
  userId: PropTypes.number.isRequired,
};
